import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as action from "../../store/actions/questionnaire-action";
import api from "../../utils/apiUtils";
import getInitialQuestionnaireId from "../../utils/initConfig";
import { ChevronRight } from "../../components/icons/ChevronRight";

const Home = () => {
  let dispatch = useDispatch();
  let navigate = useNavigate();

  const onStart = async () => {
    const questionnaireId = await getInitialQuestionnaireId();
    if (!questionnaireId) {
      alert("Error: Questionnaire not found");
      return;
    }
    api
      .get(`/questionnaires/` + questionnaireId)
      .then((response) => {
        dispatch({ type: action.LOAD_QUESTIONNAIRE, payload: response.data });
        navigate("/landing-page");
      })
      .catch((err) => {
        console.error("Failed to load questionnaire:", err);
        alert("Error: Failed to load questionnaire");
      });
  };


  return (
    <div className="w-full min-h-screen bg-[#1C1D1F]">
      {/* Hero Section - Full viewport height */}
      <div className="relative w-full min-h-screen bg-[#1C1D1F] overflow-hidden flex items-center justify-center">

        {/* Section 2: Text Content - Properly centered */}
        <div className="relative flex flex-col items-center z-10 px-4 sm:px-6 md:px-8 py-20">
          <div className="flex flex-col items-center gap-[40px] w-full max-w-[900px]">
            {/* Eyebrow */}
            <p className="font-inter font-medium text-[14px] leading-[21px] tracking-[0.07em] uppercase text-[#249FDA] text-center">
              SMARTCAT
            </p>

            {/* Main Heading */}
            <h1 className="font-archivo font-bold text-[32px] sm:text-[44px] md:text-[60px] leading-[1.3] text-white text-center max-w-[800px]">
              AI Readiness Assessment<br />Questionnaire
            </h1>

            {/* Description */}
            <p className="font-inter font-normal text-[16px] sm:text-[18px] md:text-[20px] leading-[1.5] text-[#A7A9BE] text-center max-w-[800px]">
              Discover how prepared your organization really is for the AI era. Artificial intelligence
              is transforming every industry but not every company is equally ready to take
              advantage of it. The SmartCat AI Readiness Assessment helps you understand
              where you stand today and what it will take to move faster toward measurable
              impact with AI.
            </p>

            {/* CTA Button */}
            <button
              onClick={onStart}
              className="group hover:opacity-90 transition-opacity"
              style={{
                width: '148px',
                height: '41px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                paddingTop: '10px',
                paddingRight: '29px',
                paddingBottom: '10px',
                paddingLeft: '29px',
                gap: '2px',
                background: 'linear-gradient(91.9deg, #145F96 0.8%, #249FDA 99.45%)',
                borderRadius: '8px',
                borderWidth: '1px',
                border: 'none',
                boxSizing: 'border-box',
                opacity: 1,
                cursor: 'pointer'
              }}
            >
              <span
                style={{
                  height: '21px',
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '150%',
                  letterSpacing: '0%',
                  color: '#FFFFFF',
                  flex: 'none',
                  order: 0,
                  flexGrow: 0,
                  whiteSpace: 'nowrap'
                }}
              >
                Let's Start
              </span>
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  position: 'relative',
                  flex: 'none',
                  order: 1,
                  flexGrow: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="group-hover:translate-x-1 transition-transform"
                >
                  <path
                    d="M5.83331 14.6668L14.1666 6.3335M14.1666 6.3335H5.83331M14.1666 6.3335V14.6668"
                    stroke="white"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* Section 3: CTA section - Lights animation */}
        <div
          className="absolute left-1/2 -translate-x-1/2 bottom-0 overflow-hidden"
          style={{
            width: '100vw',
            height: '483px'
          }}
        >
          {/* Top fade overlay to blend with background - stronger fade */}
          <div
            className="absolute inset-0 z-10 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, #1C1D1F 0%, rgba(28, 29, 31, 0.8) 40%, rgba(28, 29, 31, 0.3) 70%, transparent 90%)'
            }}
          />

          {/* Animated gradient container */}
          <div className="relative w-full h-full">
            {/* Vector 1 - Large sweeping gradient base */}
            <div
              className="absolute animate-sweep-horizontal"
              style={{
                width: '1011.52px',
                height: '809.13px',
                left: '-484px',
                top: '302px',
                background: 'linear-gradient(180deg, rgba(16, 114, 186, 0.6) 0%, rgba(16, 114, 186, 0.4) 100%)',
                mixBlendMode: 'screen',
                filter: 'blur(244.391px)',
                transform: 'rotate(60deg)',
                transformOrigin: 'center center',
                opacity: '0.8'
              }}
            />

            {/* Ellipse 3 - Sweeping blue wave */}
            <div
              className="absolute animate-sweep-horizontal"
              style={{
                width: '710.7px',
                height: '265.7px',
                left: '151px',
                top: '279px',
                background: 'rgba(7, 181, 255, 0.7)',
                mixBlendMode: 'screen',
                filter: 'blur(167.606px)',
                borderRadius: '50%',
                transform: 'rotate(45.74deg)',
                transformOrigin: 'center center',
                opacity: '0.7',
                animationDelay: '1s'
              }}
            />

            {/* Ellipse 2 - Sweeping cyan orb */}
            <div
              className="absolute animate-sweep-horizontal"
              style={{
                width: '500.97px',
                height: '500.97px',
                left: '303px',
                top: '350px',
                background: 'rgba(69, 197, 224, 0.6)',
                mixBlendMode: 'screen',
                filter: 'blur(167.606px)',
                borderRadius: '50%',
                transform: 'rotate(60deg)',
                transformOrigin: 'center center',
                opacity: '0.6',
                animationDelay: '2s'
              }}
            />

            {/* Horizontal moving gradient overlay - very subtle */}
            <div
              className="absolute animate-slide-horizontal"
              style={{
                width: '200%',
                height: '100%',
                left: '-50%',
                background: 'linear-gradient(90deg, transparent 0%, rgba(36, 159, 218, 0.08) 25%, rgba(69, 197, 224, 0.1) 50%, rgba(7, 181, 255, 0.08) 75%, transparent 100%)',
                mixBlendMode: 'screen'
              }}
            />
          </div>
        </div>
      </div>

      {/* Section: A 360° Look at Your AI Readiness */}
      <div className="relative w-full flex flex-col items-center py-[60px] sm:py-[80px] md:py-[120px] bg-[#1C1D1F]">
        <div className="w-full max-w-[1200px] px-4 sm:px-6 md:px-8">
          <div className="flex flex-col items-center gap-[80px]">
            {/* Section Header */}
            <div className="flex flex-col items-center gap-[24px] max-w-[800px]">
              <p className="font-inter font-medium text-[14px] leading-[21px] tracking-[0.07em] uppercase text-[#249FDA]">
                HOW WE WORK
              </p>
              <h2 className="font-archivo font-bold text-[28px] sm:text-[36px] md:text-[44px] leading-[1.4] text-white text-center">
                A 360° Look at Your AI Readiness
              </h2>
              <p className="font-inter font-normal text-[16px] sm:text-[18px] md:text-[20px] leading-[1.5] text-[#A7A9BE] text-center">
                Unlike generic maturity tests, this framework looks at your organization through two
                complementary lenses:
              </p>
            </div>

            {/* Two Cards */}
            <div className="flex flex-col gap-[30px] max-w-[846px] w-full">
              <div className="flex flex-col gap-[16px] p-[24px] sm:p-[32px] md:p-[40px] bg-[#27272A] rounded-[16px] border border-[#3F3F46]">
                <h3 className="font-archivo font-semibold text-[20px] leading-[36px] text-white">
                  Data Value Readiness
                </h3>
                <p className="font-inter font-normal text-[16px] md:text-[18px] leading-[1.5] text-[#A7A9BE]">
                  How effectively you collect, organize, and turn data into business value
                </p>
              </div>
              <div className="flex flex-col gap-[16px] p-[24px] sm:p-[32px] md:p-[40px] bg-[#27272A] rounded-[16px] border border-[#3F3F46]">
                <h3 className="font-archivo font-semibold text-[20px] leading-[36px] text-white">
                  Agentic Readiness
                </h3>
                <p className="font-inter font-normal text-[16px] md:text-[18px] leading-[1.5] text-[#A7A9BE]">
                  How quickly and strategically you adopt Generative AI, automation, and AI agents for intelligent AI innovation
                </p>
              </div>
            </div>

            {/* Bottom Text */}
            <p className="font-inter font-normal text-[16px] sm:text-[18px] md:text-[20px] leading-[1.5] text-[#A7A9BE] text-center max-w-[800px]">
              Together, these two perspectives reveal the full picture of your AI maturity, showing not
              only how strong your foundation is, but how ready you are to operate like an AI-native
              company.
            </p>
          </div>
        </div>
      </div>

      {/* Section: Why it's worth your 10 minutes */}
      <div className="relative w-full flex flex-col items-center py-[60px] sm:py-[80px] md:py-[120px] bg-[#1C1D1F]">
        <div className="w-full max-w-[1200px] px-4 sm:px-6 md:px-8">
          <div className="flex flex-col items-center gap-[80px]">
            {/* Section Header */}
            <div className="flex flex-col items-center gap-[24px]">
              <p className="font-inter font-medium text-[14px] leading-[21px] tracking-[0.07em] uppercase text-[#249FDA]">
                QUESTIONARY
              </p>
              <h2 className="font-archivo font-bold text-[28px] sm:text-[36px] md:text-[44px] leading-[1.4] text-white text-center">
                Why it's worth your 10 minutes
              </h2>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[16px] w-full max-w-[1200px]">
              {/* Benefit 1 */}
              <div className="flex flex-col items-start pt-[24px] px-[16px] pb-[16px] gap-[10px] bg-[#27272A] rounded-[16px] min-h-[208px]">
                <div className="w-[40px] h-[40px] flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 1V17M15 3L3 15M17 9H1M15 15L3 3" stroke="#45C5E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="font-inter font-normal text-[14px] leading-[21px] text-[#A7A9BE]">
                  Get a clear, data-driven view of your current AI capabilities and gaps
                </p>
              </div>

              {/* Benefit 2 */}
              <div className="flex flex-col items-start pt-[24px] px-[16px] pb-[16px] gap-[10px] bg-[#27272A] rounded-[16px] min-h-[208px]">
                <div className="w-[40px] h-[40px] flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 1V17M15 3L3 15M17 9H1M15 15L3 3" stroke="#45C5E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="font-inter font-normal text-[14px] leading-[21px] text-[#A7A9BE]">
                  Benchmark your organization's maturity against the latest industry standards
                </p>
              </div>

              {/* Benefit 3 */}
              <div className="flex flex-col items-start pt-[24px] px-[16px] pb-[16px] gap-[10px] bg-[#27272A] rounded-[16px] min-h-[208px]">
                <div className="w-[40px] h-[40px] flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 1V17M15 3L3 15M17 9H1M15 15L3 3" stroke="#45C5E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="font-inter font-normal text-[14px] leading-[21px] text-[#A7A9BE]">
                  Receive personalized SmartCat recommendations on how to accelerate your AI adoption - from foundational data strategy to agentic AI enablement
                </p>
              </div>

              {/* Benefit 4 */}
              <div className="flex flex-col items-start pt-[24px] px-[16px] pb-[16px] gap-[10px] bg-[#27272A] rounded-[16px] min-h-[208px]">
                <div className="w-[40px] h-[40px] flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 1V17M15 3L3 15M17 9H1M15 15L3 3" stroke="#45C5E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="font-inter font-normal text-[14px] leading-[21px] text-[#A7A9BE]">
                  Walk away with a practical roadmap for improving efficiency, decision-making, and innovation
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section: Your AI future starts */}
      <div className="relative w-full flex flex-col items-center py-[60px] sm:py-[80px] md:py-[120px] bg-[#1C1D1F]">
        <div className="w-full max-w-[1200px] px-4 sm:px-6 md:px-8">
          <div className="flex flex-col items-center gap-[80px]">
            {/* Section Header */}
            <div className="flex flex-col items-center gap-[24px] max-w-[800px] text-center">
              <p className="font-inter font-medium text-[14px] leading-[21px] tracking-[0.07em] uppercase text-[#249FDA]">
                HOW IT WORKS
              </p>
              <h2 className="font-archivo font-bold text-[28px] sm:text-[36px] md:text-[44px] leading-[1.2] text-white text-center">
                Your AI future starts with understanding<br />where you are today.
              </h2>
              <p className="font-inter font-normal text-[14px] sm:text-[16px] leading-[1.7] text-[#A7A9BE] text-center">
                Take the assessment and see how close you are to becoming an AI-powered <br className="hidden sm:block" />organization.
              </p>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px] w-full">
              {/* Step 1 */}
              <div className="flex flex-col items-start p-[16px] sm:p-[20px] gap-[12px] bg-[#27272A] rounded-[16px] border border-[#249FDA] shadow-[0px_24px_48px_rgba(14,14,15,0.25)] min-h-[180px]">
                <span className="font-archivo font-bold text-[20px] leading-[24px] text-white">Step 1.</span>
                <p className="font-inter font-normal text-[16px] leading-[21px] text-[#A7A9BE]">
                  Answer 19 quick multiple-choice questions - it takes about 10-15 minutes
                </p>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col items-start p-[16px] sm:p-[20px] gap-[12px] bg-[#27272A] rounded-[16px] border border-[#249FDA] shadow-[0px_24px_48px_rgba(14,14,15,0.25)] min-h-[180px]">
                <span className="font-archivo font-bold text-[20px] leading-[24px] text-white">Step 2.</span>
                <p className="font-inter font-normal text-[16px] leading-[21px] text-[#A7A9BE]">
                  Evaluate your company across data, analytics, and agentic AI adoption dimensions
                </p>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col items-start p-[16px] sm:p-[20px] gap-[12px] bg-[#27272A] rounded-[16px] border border-[#249FDA] shadow-[0px_24px_48px_rgba(14,14,15,0.25)] min-h-[180px]">
                <span className="font-archivo font-bold text-[20px] leading-[24px] text-white">Step 3.</span>
                <p className="font-inter font-normal text-[16px] leading-[21px] text-[#A7A9BE]">
                  Instantly receive a dual score showing your Data Readiness and Agentic Readiness levels
                </p>
              </div>

              {/* Step 4 */}
              <div className="flex flex-col items-start p-[16px] sm:p-[20px] gap-[12px] bg-[#27272A] rounded-[16px] border border-[#249FDA] shadow-[0px_24px_48px_rgba(14,14,15,0.25)] min-h-[180px]">
                <span className="font-archivo font-bold text-[20px] leading-[24px] text-white">Step 4.</span>
                <p className="font-inter font-normal text-[16px] leading-[21px] text-[#A7A9BE]">
                  Get your personalized AI Readiness Report with clear next steps for measurable progress
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;